import { useId } from "react";
import Select, { GroupBase, Props as SelectProps } from "react-select";
import { useFormContext, Controller } from "react-hook-form";
import { useIntl } from "react-intl";
import Reload from "./icons/Reload";
import classnames from "classnames";

interface OptionType {
  value: number | string;
  label: string;
}

interface Field {
  name: string;
  value: string | number;
  onChange: (value: string | number | null) => void;
}

interface DataFetchProps<T, Option = OptionType> {
  isError?: boolean;
  error?: string | null;
  isLoading?: boolean;
  data: T[];
  refetch?: () => void;
  optionMapper: (item: T) => Option;
}

interface Props<
  Option extends { value: number | string } = OptionType,
  Group extends GroupBase<Option> = GroupBase<Option>
> extends Omit<SelectProps<Option, false, Group>, "options">,
    DataFetchProps<any, Option> {
  name: string;
  label?: string;
  options?: Option[];
}

const BaseSelect = <
  Option extends { value: number | string } = OptionType,
  Group extends GroupBase<Option> = GroupBase<Option>
>({
  name,
  label,
  options,
  data,
  isLoading,
  isError,
  error,
  refetch,
  optionMapper,
  ...selectProps
}: Props<Option, Group>) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const intl = useIntl();
  const id = useId();

  const formError = errors[name]?.message as string | undefined;

  const placeholder = isLoading
    ? intl.formatMessage({ id: "customComponents.loading" })
    : selectProps.placeholder;

  const computedOptions = data?.map(optionMapper) || [];

  const handleChange = (
    option: Option | null,
    onChange: (value: string | number | null) => void
  ) => {
    onChange(option ? option.value : null);
  };

  const renderSelect = ({ field }: { field: Field }) => {
    const selectedOption =
      computedOptions.find((option: Option) => option.value === field.value) ||
      null;
    return (
      <Select
        inputId={id}
        {...selectProps}
        options={computedOptions}
        value={selectedOption}
        onChange={(option: Option | null) =>
          handleChange(option, field.onChange)
        }
        placeholder={placeholder}
        isDisabled={isLoading || isError || selectProps.isDisabled}
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    );
  };

  return (
    <div className="base-select">
      {label && (
        <label htmlFor={id} className="usadmin-base-select__label">
          {label}
        </label>
      )}
      <Controller name={name} control={control} render={renderSelect} />
      {formError && <p className="usadmin-base-input__error">{formError}</p>}
      {isError && (
        <div className="usadmin-base-select__error-message--wrapper">
          <span className="usadmin-base-input__error">{error}</span>
          <button
            type="button"
            onClick={() => refetch && refetch()}
            className="usadmin-base-select__refetch-button"
          >
            {intl.formatMessage({ id: "usAdmin.refetch" })}
            <Reload
              className={classnames(
                "usadmin-base-input__loading",
                isLoading && "usadmin__loading-animation"
              )}
            />
          </button>
        </div>
      )}
    </div>
  );
};

export default BaseSelect;
